import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  WizardModalFooter,
  WizardStepBody,
  WizardStepper,
} from '@/components/wizards';
import { useWizardFooterConfig } from '@/components/wizards';
import { useExportLOA } from '@/hooks/useReportExport';
import {
  RiskModeStep,
  RiskScopeConfigStep,
} from '@/sections/export/components/RiskSelectionStep';
import {
  RiskSelectionMode,
  useRiskSelection,
} from '@/sections/export/hooks/useRiskSelection';
import { useAuth } from '@/state/auth';
import { RiskStatus } from '@/types';

const DEFAULT_LICENSING_LINE =
  'Praetorian is independently certified by **CREST** to conduct Penetration Testing services.';

const getTodayISO = (): string => new Date().toISOString().split('T')[0];

// defaultContent seeds the editable LOA body. %%FINDINGS%% is expanded
// server-side with the severity breakdown computed from the selected risks.
// (No date placeholders here — the wizard's risk scope is the source of the
// covered findings, and the issue date is captured as its own field.)
function defaultContent(clientName: string): string {
  const c = clientName.trim() || 'the client';
  return `## Security Evaluation Performed by Independent Experts

The undersigned attests that Praetorian completed a security assessment of the ${c} environment. This assessment involved a time-boxed, best-effort approach covering the in-scope systems.

During the assessment, Praetorian identified the following issues:

%%FINDINGS%%

The testing methodology was informed by industry-standard frameworks and best practices, including the OWASP Web Security Testing Guide, the OWASP Top Ten (2021), and the Penetration Testing Execution Standard (PTES). All findings were assessed and rated using the Common Vulnerability Scoring System (CVSS) framework to ensure consistent and standardized risk evaluation.

As ${c}'s business continues to change, so will its overall security posture. Such changes will affect the validity of Praetorian's findings and this letter. Therefore, any statements made by Praetorian only describe a "snapshot" in time.

Praetorian would like to thank ${c} for this opportunity to help the organization evaluate its current security posture.`;
}

interface LOAFormValues {
  clientName: string;
  assessmentName: string;
  issueDate: string;
  undersignedName: string;
  undersignedDesignation: string;
  undersignedEmail: string;
  licensingLine: string;
  content: string;
  draft: boolean;
}

type StepId =
  | 'scope'
  | 'scopeConfig'
  | 'client'
  | 'signatory'
  | 'letter'
  | 'review';

const STEP_TITLES: Record<StepId, string> = {
  scope: 'Select risks',
  scopeConfig: 'Configure scope',
  client: 'Client & assessment',
  signatory: 'Signatory',
  letter: 'Letter content',
  review: 'Review & generate',
};

const STEP_DESCRIPTIONS: Record<StepId, string> = {
  scope: 'Which risks should this Letter of Attestation cover?',
  scopeConfig: 'Configure the risk scope for this attestation.',
  client: 'Who is this attestation for?',
  signatory: 'Who is signing this attestation?',
  letter: 'Edit the attestation letter body.',
  review: 'Confirm the scope and details, then generate the LOA.',
};

// `scopeConfig` exists only when the chosen mode needs configuring; `all` has
// no config, so Next on `scope` skips straight to `details`. Recomputed from
// the live mode each render, so the Configure dot appears/disappears
// automatically (the stepper is pure-presentational).
function buildSteps(mode: RiskSelectionMode): StepId[] {
  return [
    'scope',
    ...(mode === 'all' ? [] : (['scopeConfig'] as StepId[])),
    'client',
    'signatory',
    'letter',
    'review',
  ];
}

// Mode-aware copy for the config screen.
function scopeConfigTitle(mode: RiskSelectionMode): string {
  if (mode === 'dateRange') return 'Date range';
  if (mode === 'byAsset') return 'Select assets';
  return 'Select vulnerabilities';
}
function scopeConfigDescription(mode: RiskSelectionMode): string {
  if (mode === 'dateRange')
    return 'Pick the date field and window this attestation should cover.';
  if (mode === 'byAsset')
    return 'Choose the assets whose open risks this attestation should cover.';
  return 'Hand-pick the exact risks this attestation should cover.';
}

export interface GenerateLOAWizardProps {
  /** Externally controlled open state (the LOA tab renders its own trigger). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * GenerateLOAWizard — Praetorian-only multi-step wizard that generates a Letter
 * of Attestation. The flow is: select the risk scope (via the shared
 * `useRiskSelection` hook + `RiskSelectionStep`), optionally configure that
 * scope (date range / hand-picked risks), capture the client & assessment
 * details, the signatory, then the letter content, and finally review and
 * submit. Posts to /export/loa via `useExportLOA`, mirroring the legacy
 * `GenerateLOA` modal's payload exactly.
 *
 * Externally controlled only — the parent (the LOA tab) owns `open` and renders
 * the "Generate LOA" trigger.
 */
export function GenerateLOAWizard({
  open,
  onOpenChange,
}: GenerateLOAWizardProps) {
  const { isPraetorianUser } = useAuth();

  if (!isPraetorianUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        // FilterDropdown's panel is portalled to document.body (see
        // FilterDropdown.tsx). Because that portal is a DOM sibling of this
        // DialogContent, Radix's DismissableLayer treats clicks inside the
        // dropdown as `pointerDownOutside` and dismisses the whole wizard.
        // The FilterDropdown contract stamps every portalled node with
        // `data-filter-dropdown-portal`; we honor it here so clicks inside
        // the dropdown are treated as inside the dialog while normal
        // outside-click-to-close still works everywhere else.
        onInteractOutside={event => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-filter-dropdown-portal]')) {
            event.preventDefault();
          }
        }}
      >
        {/* key={String(open)} fully resets wizard state each time it reopens. */}
        <GenerateLOAWizardForm
          key={String(open)}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function GenerateLOAWizardForm({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const selection = useRiskSelection();
  const { mutateAsync: exportLOA, status } = useExportLOA();
  const isGenerating = status === 'pending';

  // Navigate by a STABLE step key, not a numeric index: the step list changes
  // length with the mode (3 keys for `all`, 4 otherwise). The current key stays
  // valid across mode changes because mode can only change on `scope`.
  const [currentStep, setCurrentStep] = useState<StepId>('scope');
  const [form, setForm] = useState<LOAFormValues>(() => ({
    clientName: '',
    assessmentName: '',
    issueDate: getTodayISO(),
    undersignedName: '',
    undersignedDesignation: '',
    undersignedEmail: me,
    licensingLine: DEFAULT_LICENSING_LINE,
    content: defaultContent(''),
    draft: false,
  }));
  const [contentEdited, setContentEdited] = useState(false);

  const steps = buildSteps(selection.mode);
  const currentIndex = Math.max(0, steps.indexOf(currentStep));
  const isLastStep = currentIndex === steps.length - 1;

  // The client name gates Next out of the details step; required by the export.
  // 'scope' always advances. 'scopeConfig' is gated on
  // `!selection.isResolving`/`isLoadingOptions` so the user can't carry a
  // partially-paged (capped) date-range key set forward — the date-range scope
  // must fully resolve first. See useRiskSelection.
  const clientNameValid = form.clientName.trim() !== '';
  const contentValid = form.content.trim() !== '';
  // A dateRange scope with blank bounds resolves to `undefined` (= all open
  // risks), so block Next until both dates are set. `specific` intentionally
  // allows an empty selection (= all open risks); `all` never reaches this step.
  // byAsset must resolve to at least one risk before advancing: an empty
  // `risk_keys` payload is interpreted server-side as "all open risks", so a
  // scoped selection that resolved to zero would silently cover everything.
  // For `specific` mode we also gate on `isSpecificScopeReady`, which blocks
  // the zero-match tag-filter case (tag selected but matched zero open risks:
  // resolves to []; backend treats that as "all open risks" and would cover
  // everything). See useRiskSelection.
  const scopeConfigReady =
    selection.mode === 'dateRange'
      ? Boolean(selection.startDate && selection.endDate)
      : selection.mode === 'byAsset'
        ? selection.selectedAssetKeys.length > 0 &&
          (selection.resolvedRiskKeys?.length ?? 0) > 0
        : selection.mode === 'specific'
          ? selection.isSpecificScopeReady
          : true;
  const canGoNext =
    currentStep === 'scope'
      ? true
      : currentStep === 'scopeConfig'
        ? scopeConfigReady &&
          !selection.isResolving &&
          !selection.isLoadingOptions &&
          !selection.isLoadingAssetOptions
        : currentStep === 'client'
          ? clientNameValid
          : currentStep === 'signatory'
            ? true
            : currentStep === 'letter'
              ? contentValid
              : clientNameValid && contentValid && !selection.isResolving;

  function setField<K extends keyof LOAFormValues>(
    field: K,
    value: LOAFormValues[K]
  ) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Keep the body template in sync with the client name until the user edits
      // the body themselves.
      if (field === 'clientName' && !contentEdited) {
        next.content = defaultContent(next.clientName);
      }
      return next;
    });
  }

  async function handleGenerate() {
    try {
      await exportLOA({
        risk_keys: selection.resolvedRiskKeys,
        status_filter: [RiskStatus.Opened],
        config: {
          client_name: form.clientName,
          assessment_name: form.assessmentName,
          issue_date: form.issueDate,
          undersigned_name: form.undersignedName,
          undersigned_designation: form.undersignedDesignation,
          undersigned_email: form.undersignedEmail,
          licensing_line: form.licensingLine,
          content: form.content,
          draft: form.draft,
        },
      });
      onClose();
    } catch {
      // toast handled by useExportLOA; keep the wizard open so the user can retry.
    }
  }

  // Next/Back move by key: step the current key's position in the (mode-aware)
  // step list by one, clamp, and set the resulting key.
  const goToOffset = (offset: number) => {
    const next = Math.min(Math.max(currentIndex + offset, 0), steps.length - 1);
    setCurrentStep(steps[next]);
  };

  const footerConfig = useWizardFooterConfig({
    canGoNext,
    canGoBack: currentIndex > 0,
    isPending: isGenerating,
    isLastStep,
    onNext: () => goToOffset(1),
    onBack: () => goToOffset(-1),
    onCancel: onClose,
    onSubmit: handleGenerate,
    labels: { submit: 'Generate LOA', submitting: 'Generating...' },
  });

  const scopeSummary = describeScope(selection);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Letter of Attestation</DialogTitle>
        <DialogDescription>
          {currentStep === 'scopeConfig'
            ? scopeConfigDescription(selection.mode)
            : STEP_DESCRIPTIONS[currentStep]}
        </DialogDescription>
      </DialogHeader>

      <WizardStepper
        steps={steps.map(s => ({
          id: s,
          title:
            s === 'scopeConfig'
              ? scopeConfigTitle(selection.mode)
              : STEP_TITLES[s],
        }))}
        currentStep={currentIndex + 1}
        variant="dots"
      />

      {currentStep === 'scope' && (
        <WizardStepBody align="center">
          <RiskModeStep selection={selection} />
        </WizardStepBody>
      )}

      {currentStep === 'scopeConfig' && (
        <WizardStepBody
          align={
            selection.mode === 'specific' || selection.mode === 'byAsset'
              ? 'top'
              : 'center'
          }
        >
          <RiskScopeConfigStep selection={selection} />
        </WizardStepBody>
      )}

      {currentStep === 'client' && (
        <WizardStepBody align="center">
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loa-client-name">Client Name</Label>
                <Input
                  id="loa-client-name"
                  type="text"
                  value={form.clientName}
                  onChange={e => setField('clientName', e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loa-issue-date">Issue Date</Label>
                <Input
                  id="loa-issue-date"
                  type="date"
                  value={form.issueDate}
                  onChange={e => setField('issueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="loa-assessment-name">Assessment Name</Label>
              <Input
                id="loa-assessment-name"
                type="text"
                value={form.assessmentName}
                onChange={e => setField('assessmentName', e.target.value)}
                placeholder="2026 Application and External Network Penetration Test"
              />
            </div>
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'signatory' && (
        <WizardStepBody align="center">
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loa-undersigned-name">Undersigned Name</Label>
                <Input
                  id="loa-undersigned-name"
                  type="text"
                  value={form.undersignedName}
                  onChange={e => setField('undersignedName', e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loa-undersigned-title">Title</Label>
                <Input
                  id="loa-undersigned-title"
                  type="text"
                  value={form.undersignedDesignation}
                  onChange={e =>
                    setField('undersignedDesignation', e.target.value)
                  }
                  placeholder="Offensive Security Manager, Praetorian"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loa-undersigned-email">Email</Label>
                <Input
                  id="loa-undersigned-email"
                  type="email"
                  value={form.undersignedEmail}
                  onChange={e => setField('undersignedEmail', e.target.value)}
                  placeholder="jane.doe@praetorian.com"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="loa-licensing-line">Licensing Line</Label>
              <Input
                id="loa-licensing-line"
                type="text"
                value={form.licensingLine}
                onChange={e => setField('licensingLine', e.target.value)}
              />
            </div>
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'letter' && (
        <WizardStepBody align="top">
          <div className="flex h-full flex-col gap-3">
            <Label htmlFor="loa-content">Content</Label>
            <Textarea
              id="loa-content"
              className="min-h-0 flex-1 font-mono"
              value={form.content}
              onChange={e => {
                setContentEdited(true);
                setField('content', e.target.value);
              }}
            />
            <p className="text-chariot-text-secondary text-xs">
              Markdown. <code className="text-xs">%%FINDINGS%%</code> is
              replaced with the severity breakdown computed from the selected
              risks.
            </p>
            <div className="flex items-center justify-between py-1">
              <Label htmlFor="loa-draft">Draft watermark</Label>
              <Switch
                id="loa-draft"
                checked={form.draft}
                onCheckedChange={checked => setField('draft', checked)}
                aria-label="Draft watermark"
              />
            </div>
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'review' && (
        <WizardStepBody align="center">
          <div className="flex flex-col gap-4">
            <div className="border-chariot-stroke flex flex-col gap-3 rounded-lg border p-4">
              <SummaryRow label="Risk scope" value={scopeSummary} />
              <SummaryRow
                label="Client name"
                value={form.clientName.trim() || '—'}
              />
              <SummaryRow
                label="Assessment name"
                value={form.assessmentName.trim() || '—'}
              />
              <SummaryRow label="Issue date" value={form.issueDate || '—'} />
              <SummaryRow
                label="Undersigned"
                value={
                  form.undersignedName.trim()
                    ? `${form.undersignedName.trim()}${
                        form.undersignedDesignation.trim()
                          ? ` — ${form.undersignedDesignation.trim()}`
                          : ''
                      }`
                    : '—'
                }
              />
              {form.draft && <SummaryRow label="Watermark" value="Draft" />}
            </div>
            <p className="text-chariot-text-secondary text-sm">
              Generating renders the Letter of Attestation server-side and
              starts an automatic download when it&apos;s ready.
            </p>
          </div>
        </WizardStepBody>
      )}

      <WizardModalFooter config={footerConfig} />
    </>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-chariot-text-secondary text-sm">{label}</span>
      <span className="text-chariot-text-primary min-w-0 flex-1 text-right text-sm break-words">
        {value}
      </span>
    </div>
  );
}

// describeScope renders a human-readable summary of the resolved risk scope.
// `resolvedRiskKeys === undefined` means "all open risks" (the hook's contract).
function describeScope(selection: ReturnType<typeof useRiskSelection>): string {
  if (selection.isResolving) {
    return 'Resolving matching risks…';
  }
  const keys = selection.resolvedRiskKeys;
  if (keys === undefined) {
    return 'All open risks';
  }
  return `${keys.length} risk${keys.length === 1 ? '' : 's'}`;
}
